import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { publicApi } from '@/services/publicApi';

export interface GeneralSettings {
  site_name: string;
  tagline: string;
  contact_email: string;
  contact_phone: string;
  primary_phone: string;
  primary_phone_raw: string;
  whatsapp_number: string;
  address: string;
  currency: string;
  date_format: string;
  logo_url: string;
  favicon_url: string;
  show_site_name: boolean;
}

export interface FeatureToggles {
  property_ratings: boolean;
  enquiries_count: boolean;
  posted_date: boolean;
  user_registration: boolean;
  require_email: boolean;
  show_prices: boolean;
  karnataka_cities_page: boolean;
}

export interface SocialLinks {
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  youtube: string;
}

export interface SiteSettings {
  general: GeneralSettings;
  features: FeatureToggles;
  social: SocialLinks;
}

const defaultSettings: SiteSettings = {
  general: {
    site_name: 'CashMyProperty.ai',
    tagline: 'AI-Powered Land Monetization',
    contact_email: 'support@cashmyproperty.ai',
    contact_phone: '+91 90350 63455',
    primary_phone: '+91 90350 63455',
    primary_phone_raw: '+919035063455',
    whatsapp_number: '+919035063455',
    address: 'Bengaluru, Karnataka, India',
    currency: 'INR',
    date_format: 'DD/MM/YYYY',
    logo_url: '',
    favicon_url: '',
    show_site_name: true,
  },
  features: {
    property_ratings: true,
    enquiries_count: true,
    posted_date: true,
    user_registration: true,
    require_email: false,
    show_prices: true,
    karnataka_cities_page: true,
  },
  social: {
    facebook: 'https://facebook.com/cashmyproperty',
    instagram: 'https://instagram.com/cashmyproperty',
    twitter: 'https://x.com/cashmyproperty',
    linkedin: 'https://linkedin.com/company/cashmyproperty',
    youtube: 'https://youtube.com/@cashmyproperty',
  },
};

interface SettingsContextValue {
  settings: SiteSettings;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await publicApi.getSettings();
      if (res?.data) {
        setSettings(prev => ({
          general: { ...prev.general, ...(res.data.general as Partial<GeneralSettings>) },
          features: { ...prev.features, ...(res.data.features as Partial<FeatureToggles>) },
          social: { ...prev.social, ...(res.data.social as Partial<SocialLinks>) },
        }));
      }
    } catch {
      // Public API unavailable — use defaults silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const value = useMemo<SettingsContextValue>(() => ({
    settings,
    loading,
    error,
    refresh: loadSettings,
  }), [settings, loading, error, loadSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    // Return defaults rather than throwing — allows graceful degradation
    return { settings: defaultSettings, loading: false, error: null, refresh: async () => {} };
  }
  return ctx;
}